// packages/web/src/lib/api/retry.ts
// Exponential backoff retry logic for API calls

export interface RetryOptions {
  maxRetries?: number;
  baseDelay?: number;
  maxDelay?: number;
  backoffFactor?: number;
  retryCondition?: (error: any, attempt: number) => boolean;
  onRetry?: (error: any, attempt: number) => void;
}

export class ApiRetryError extends Error {
  constructor(
    message: string,
    public readonly lastError: any,
    public readonly attempts: number
  ) {
    super(message);
    this.name = 'ApiRetryError';
  }
}

/**
 * Default retry condition - retry on network errors and 5xx status codes
 */
function defaultRetryCondition(error: any, attempt: number): boolean {
  // Don't retry on client errors (4xx)
  if (error.status && error.status >= 400 && error.status < 500) {
    return false;
  }

  // Retry on network errors, timeouts, and server errors (5xx)
  if (error.name === 'TypeError' || error.message?.includes('fetch')) {
    return true; // Network error
  }

  if (error.status && error.status >= 500) {
    return true; // Server error
  }

  // Retry on specific error messages
  if (error.message?.includes('Service Unavailable') ||
      error.message?.includes('Internal Server Error') ||
      error.message?.includes('Bad Gateway') ||
      error.message?.includes('Gateway Timeout')) {
    return true;
  }

  return false;
}

/**
 * Calculate delay with exponential backoff and jitter
 */
function calculateDelay(attempt: number, baseDelay: number, maxDelay: number, backoffFactor: number): number {
  const exponentialDelay = baseDelay * Math.pow(backoffFactor, attempt);
  const delayWithJitter = exponentialDelay * (0.5 + Math.random() * 0.5); // Add jitter
  return Math.min(delayWithJitter, maxDelay);
}

/**
 * Execute a function with retry logic
 */
export async function withRetry<T>(
  operation: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const {
    maxRetries = 3,
    baseDelay = 1000,
    maxDelay = 10000,
    backoffFactor = 2,
    retryCondition = defaultRetryCondition,
    onRetry
  } = options;

  let lastError: any;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      console.log(`[API RETRY] Attempt ${attempt + 1}/${maxRetries + 1}`);
      const result = await operation();

      if (attempt > 0) {
        console.log(`[API RETRY] Success on attempt ${attempt + 1}`);
      }

      return result;

    } catch (error) {
      lastError = error;

      console.warn(`[API RETRY] Attempt ${attempt + 1} failed:`, error.message);

      // Don't retry if this is the last attempt
      if (attempt >= maxRetries) {
        break;
      }

      // Check if we should retry this error
      if (!retryCondition(error, attempt)) {
        console.log(`[API RETRY] Not retrying - condition not met`);
        break;
      }

      // Calculate delay and wait
      const delay = calculateDelay(attempt, baseDelay, maxDelay, backoffFactor);
      console.log(`[API RETRY] Retrying in ${delay}ms...`);

      // Call retry callback if provided
      if (onRetry) {
        try {
          onRetry(error, attempt);
        } catch (callbackError) {
          console.warn('[API RETRY] Retry callback failed:', callbackError);
        }
      }

      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  // All retries exhausted
  throw new ApiRetryError(
    `Operation failed after ${maxRetries + 1} attempts`,
    lastError,
    maxRetries + 1
  );
}

/**
 * Wrapper for fetch requests with retry logic
 */
export async function fetchWithRetry(
  url: string,
  options: RequestInit = {},
  retryOptions: RetryOptions = {}
): Promise<Response> {
  return withRetry(
    async () => {
      const response = await fetch(url, options);

      // Throw error for non-ok responses so they can be retried
      if (!response.ok) {
        const error: any = new Error(`HTTP ${response.status}: ${response.statusText}`);
        error.status = response.status;
        error.response = response;
        throw error;
      }

      return response;
    },
    {
      ...retryOptions,
      onRetry: (error, attempt) => {
        console.log(`[FETCH RETRY] Retrying ${options.method || 'GET'} ${url} (attempt ${attempt + 1})`);
        if (retryOptions.onRetry) {
          retryOptions.onRetry(error, attempt);
        }
      }
    }
  );
}

/**
 * Enhanced apiFetch with built-in retry logic
 */
export async function apiFetchWithRetry(
  url: string,
  options: RequestInit = {},
  retryOptions: RetryOptions = {}
): Promise<any> {
  // Import apiFetch dynamically to avoid circular imports
  const { apiFetch } = await import('./fetch-helper');

  return withRetry(
    () => apiFetch(url, options),
    {
      maxRetries: 2, // Fewer retries for authenticated requests
      baseDelay: 500,
      ...retryOptions,
      onRetry: (error, attempt) => {
        console.log(`[APIFETCH RETRY] Retrying ${options.method || 'GET'} ${url} (attempt ${attempt + 1})`);
        if (retryOptions.onRetry) {
          retryOptions.onRetry(error, attempt);
        }
      }
    }
  );
}

/**
 * Create a debounced version of a function to prevent duplicate requests
 */
export function debounceRequests<T extends any[], R>(
  fn: (...args: T) => Promise<R>,
  delay: number = 1000
): (...args: T) => Promise<R> {
  let timeoutId: NodeJS.Timeout | null = null;
  let pendingPromise: Promise<R> | null = null;

  return (...args: T): Promise<R> => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    return new Promise((resolve, reject) => {
      timeoutId = setTimeout(async () => {
        try {
          if (!pendingPromise) {
            pendingPromise = fn(...args);
          }
          const result = await pendingPromise;
          resolve(result);
        } catch (error) {
          reject(error);
        } finally {
          pendingPromise = null;
        }
      }, delay);
    });
  };
}

/**
 * Request deduplication cache
 */
const requestCache = new Map<string, Promise<any>>();

export function deduplicateRequests<T>(
  key: string,
  operation: () => Promise<T>
): Promise<T> {
  if (requestCache.has(key)) {
    console.log(`[DEDUPE] Using cached request for: ${key}`);
    return requestCache.get(key)!;
  }

  const promise = operation().finally(() => {
    // Clean up cache after request completes
    setTimeout(() => requestCache.delete(key), 100);
  });

  requestCache.set(key, promise);
  return promise;
}